import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import smoothscroll from "smoothscroll-polyfill";

import SearchParams from "simplydo/core/searchParams";
import { usePageView } from "simplydo/react/hooks";

import api from "api";
import { useCurrentUser } from "utils/currentUser";
import { useClearNotifications } from "utils/useClearNotifications";
import util from "utils/utils";

import NotFound from "components/NotFound";
import { useEditControls } from "components/ideas/hooks/useEditControls";
import { useIdeaEditor } from "components/ideas/hooks/useIdeaEditor";
import PageLoadError from "components/lib/PageLoadError";

import IdeaBody from "./Body";
import AssignIdeaModal from "./Components/AssignIdea";
import IdeaHelmet from "./Components/IdeaHelmet";
import IdeaHeader from "./Header";
import { IdeaProvider } from "./IdeaProvider";
import IdeaSidebar from "./IdeaSidebar/IdeaSidebar";
import IdeaToolbar from "./IdeaToolbar";
import { useIdea, useIdeaIdState } from "./hooks/useIdea";
import type { IdeaAssignRequest } from "./types";
import type { ItemRefs } from "./useIdeaScrollTracking";

smoothscroll.polyfill();

export const IdeaPage = () => {
  const user = useCurrentUser();
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    ideaState,
    updateIdea,
    reloadIdea,
    undoChanges,
    redoChanges,
    canUndo,
    canRedo,
    otherUsers,
    websocketConnected,
    externalChanges,
  } = useIdeaEditor();
  const { unsaved, isSaving, errorMessage, notFound, savedAt } = ideaState;
  const [idea, ideaQuery] = useIdea();
  const [ideaId] = useIdeaIdState();
  useClearNotifications("idea", ideaId);
  usePageView(api, "idea", ideaId, !!idea);

  const userId = user?._id;

  const editControls = useEditControls(user, idea);
  const [editState, setEditing] = editControls;

  const [dismissedAssignModal, setDismissedAssignModal] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  const sectionRefs = useRef<ItemRefs>({});
  const setSectionRef = useCallback(
    (ref: HTMLDivElement | null, itemId: string) => {
      sectionRefs.current[itemId] = ref;
    },
    [],
  );

  const fieldRefs = useRef<ItemRefs>({});
  const setFieldRef = useCallback(
    (ref: HTMLDivElement | null, itemId: string) => {
      fieldRefs.current[itemId] = ref;
    },
    [],
  );

  const smsAuthToken = useMemo(() => {
    const params = new SearchParams(window.location.search);
    return params.get("smsAuthToken");
  }, []);
  const shouldShowAssignIdeaModal = Boolean(
    !dismissedAssignModal && userId && smsAuthToken && !idea?.user,
  );

  /*
    Once idea is loaded, scroll to the part of the page requested
  */
  useEffect(() => {
    let hashTimer: ReturnType<typeof setTimeout> | undefined;
    if (ideaQuery.isSuccess) {
      hashTimer = setTimeout(() => {
        const { hash } = document.location;
        if (hash) {
          const elem = document.getElementById(hash.replace("#", ""));
          if (elem) elem.scrollIntoView();
        }
      }, 500);
    }
    return () => {
      if (hashTimer) {
        clearTimeout(hashTimer);
      }
    };
  }, [ideaQuery.isSuccess]);

  // Attempt to scroll to where the user was previously editing, when changing into preview mode
  // Whenever the user switches to preview, we save their scroll position when editing. Once returned to editing, we scroll back there
  const [scrollPosition, setScrollPosition] = useState(0);
  const [editingScrollPosition, setEditingScrollPosition] = useState(0);
  const handleScroll = useCallback(() => {
    const position = window.pageYOffset;
    setScrollPosition(position);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setEditingAndScroll = useCallback(
    (newIsEditing: boolean) => {
      setEditing(newIsEditing);
      if (!newIsEditing) {
        setEditingScrollPosition(scrollPosition);
      } else {
        if (timeout.current) {
          clearTimeout(timeout.current);
          timeout.current = null;
        }
        timeout.current = setTimeout(() => {
          window.scrollTo({
            top: editingScrollPosition,
            left: 0,
            behavior: "smooth",
          });
        }, 500);
      }
    },
    [editingScrollPosition, scrollPosition, setEditing],
  );

  const onSubmit = useCallback(
    (isSubmitted: boolean) => {
      updateIdea(isSubmitted, ["isSubmitted"], true);
    },
    [updateIdea],
  );

  const challengeId = idea?.challenge;
  const deleteIdea = useCallback(() => {
    util
      .confirm(t("ideas.delete.title"), t("ideas.delete.info"))
      .then(() => {
        if (!ideaId) return;
        api.ideas.delete(
          ideaId,
          () => {
            api.invalidateQueries(api.challenges.get, [challengeId]);
            api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
            api.invalidateQueries(api.challenges.getIdeaImpacts, [challengeId]);
            toast(
              t("generic.typeDeleted", {
                type: t("common:capitalise", { key: "generic.idea" }),
              }),
            );
            navigate("/ideas");
          },
          (err) => toast.error(err.message),
        );
      })
      .catch(() => {});
  }, [ideaId, t, navigate, challengeId]);

  const leaveIdea = useCallback(() => {
    util
      .confirm(
        t("ideas.teammates.leave.title"),
        t("ideas.teammates.leave.info"),
      )
      .then(() => {
        if (!ideaId || !userId) {
          return;
        }
        api.ideas.removeCollaborator(
          ideaId,
          userId,
          ({ authors, collaborators }) => {
            updateIdea(authors, ["authors"], true);
            updateIdea(collaborators, ["collaborators"], true);
            reloadIdea();
          },
          (err) => toast.error(err.message),
        );
      })
      .catch(() => {});
  }, [ideaId, userId, t, updateIdea, reloadIdea]);

  const assignIdea = useCallback(() => {
    if (!smsAuthToken) {
      toast.error(t("ideas.errorNoUser"));
      return;
    }
    if (!ideaId) {
      return;
    }

    setIsAssigning(true);
    const data: IdeaAssignRequest = { smsAuthToken, authType: "sms" };
    api.ideas.assign(
      ideaId,
      data,
      () => {
        setDismissedAssignModal(true);
        setIsAssigning(false);
        updateIdea(userId, ["user"], true);
        updateIdea([user], ["authors"], true);
        reloadIdea();
      },
      () => {
        setIsAssigning(false);
        toast.error("This SMS token appears to be invalid or expired.");
      },
    );
  }, [ideaId, reloadIdea, smsAuthToken, t, updateIdea, user, userId]);

  const removeIdeaFromMerge = useCallback(
    (
      mergeIdeaId: string,
      childIdeaId: string,
      onComplete: (mergeId: string, ideaId: string) => void = () => {},
      onError: (mergeId: string, ideaId: string) => void = () => {},
    ) => {
      const children = idea?.children ?? [];
      const ownerChildren = idea?.ownerChildren ?? [];
      const willDeleteMergeContainer = children.length <= 2;
      util
        .confirm(
          `Are you sure you want to remove this ${t("generic.idea")} from the merge?`,
          `The ${t("generic.idea")} will not be deleted and will remain in the ${t("generic.challenge")}. You can re-add it to the merge at any time.${willDeleteMergeContainer ? `The merge container will be deleted as it will only contain one ${t("generic.idea")}.` : ""}`,
        )
        .then(() => {
          api.ideas.removeIdeaFromMerge(
            mergeIdeaId,
            childIdeaId,
            () => {
              if (willDeleteMergeContainer) {
                const remainingIdea = ownerChildren.find(
                  (child) => child._id !== childIdeaId,
                );
                if (remainingIdea) {
                  navigate(`/ideas/${remainingIdea._id}`);
                }
              }
              updateIdea(
                children.filter((child) => child !== childIdeaId),
                ["children"],
                true,
              );
              updateIdea(
                ownerChildren.filter((child) => child._id !== childIdeaId),
                ["ownerChildren"],
                true,
              );
              onComplete(mergeIdeaId, childIdeaId);
            },
            (err) => {
              toast.error(err.message);
              onError(mergeIdeaId, childIdeaId);
            },
          );
        })
        .catch(() => {});
    },
    [navigate, idea?.children, idea?.ownerChildren, updateIdea, t],
  );
  if (errorMessage || notFound) {
    if (shouldShowAssignIdeaModal) {
      return (
        <div style={{ position: "relative" }}>
          <AssignIdeaModal
            isOpen={true}
            assignIdea={assignIdea}
            onClose={() => setDismissedAssignModal(true)}
            loading={isAssigning}
          />
        </div>
      );
    }

    return errorMessage ? (
      <PageLoadError title={t("ideas.errorUser")} message={errorMessage} />
    ) : (
      <NotFound />
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <IdeaHelmet />

      <AssignIdeaModal
        isOpen={shouldShowAssignIdeaModal}
        assignIdea={assignIdea}
        onClose={() => setDismissedAssignModal(true)}
        loading={isAssigning}
      />

      <IdeaToolbar
        isSaving={isSaving}
        unsaved={unsaved}
        isEditing={editState.isEditing}
        setEditing={setEditingAndScroll}
        savedAt={savedAt}
        onSubmit={onSubmit}
        otherUsers={otherUsers}
        websocketConnected={websocketConnected}
        updateIdea={updateIdea}
        deleteIdea={deleteIdea}
        leaveIdea={leaveIdea}
        undoChanges={undoChanges}
        redoChanges={redoChanges}
        canUndo={canUndo}
        canRedo={canRedo}
        reloadIdea={reloadIdea}
      />

      <IdeaSidebar
        isEditing={editState.isEditing}
        sectionRefs={sectionRefs}
        fieldRefs={fieldRefs}
        updateIdea={updateIdea}
        leaveIdea={leaveIdea}
        main={
          <>
            <IdeaHeader
              isEditing={editState.isEditing}
              updateIdea={updateIdea}
            />
            <div className="idea">
              <IdeaBody
                key={idea?._id}
                isSaving={isSaving}
                unsaved={unsaved}
                externalChanges={externalChanges}
                setSectionRef={setSectionRef}
                sectionRefs={sectionRefs}
                setFieldRef={setFieldRef}
                fieldRefs={fieldRefs}
                updateIdea={updateIdea}
                onSubmit={onSubmit}
                removeIdeaFromMerge={removeIdeaFromMerge}
                {...editState}
              />
            </div>
          </>
        }
      />
    </div>
  );
};

const IdeaPageWrapper = () => {
  return (
    <IdeaProvider>
      <IdeaPage />
    </IdeaProvider>
  );
};

export default IdeaPageWrapper;
